import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from 'antd';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import { getLeafAnalysisInfo } from '../../../Utils/analysis/leafAnalysis';
import Loading from '../../Shared/Loading';
import { useSelector } from 'react-redux';
import { exportTableToExcel } from '../../../Helpers/Excel/export';
import excelPng from '../../../assets/images/excel.png';
import moment from 'moment';


const ViewLeafAnalysis = () => {
    const { exploitationId, analysisId } = useParams();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const userRole = useSelector((state) => state.user.userRole.name);
    const userInfo = useSelector((state) => state.user.userInfo);

    const userLink = () => {
        if (userRole === 'client') {
          return '/client';
        } else if (userRole === 'admin' || userRole === 'superadmin') {
          return '/admin';
        } else if (userRole === 'domain_manager') {
          return '/domain-manager';
        } else if (userRole === 'exploitation_manager') {
          return '/exploitation-manager';
        }
      };

    useEffect(() => {
        const fetchAnalysis = async () => {
            try {
                setLoading(true);
                const data = await getLeafAnalysisInfo(analysisId);
                setAnalysis(data.data);
            } catch (error) {
                console.error('Error fetching analysis:', error);
                setError('Une erreur est survenue lors du chargement des données.');
            } finally {
                setLoading(false);
            }
        };

        fetchAnalysis();
    }, [analysisId]);

    const leafAnalysisParameters = [
        { key: 'n', label: 'Azote (N) % de MS' },
        { key: 'p', label: 'Phosphore (P) % de MS' },
        { key: 'k', label: 'Potassium (K) % de MS' },
        { key: 'mg', label: 'Magnésium (Mg) % de MS' },
        { key: 'ca', label: 'Calcium (Ca) % de MS' },
        { key: 'cl', label: 'Chlorures (Cl) % de MS' },
        { key: 'na', label: 'Sodium (Na) mg/kg de MS' },
        { key: 'zn', label: 'Zinc (Zn) mg/kg de MS' },
        { key: 'cu', label: 'Cuivre (Cu) mg/kg de MS' },
        { key: 'mn', label: 'Manganèse (Mn) mg/kg de MS' },
        { key: 'fe', label: 'Fer (Fe) mg/kg de MS' },
        { key: 'b', label: 'Bore (B) mg/kg de MS' },
    ];

    const renderValue = (value) => {
        if (value === null || value === undefined) {
            return <span className="text-gray-400 text-3xl font-bold">-</span>;
        }
        return value;
    };


    const handleExport = () => {
        const info = [
            'Analyse Foliaire',
            `Date de réalisation: ${moment(analysis.date_realisation).format('DD/MM/YYYY')}`,
            `Laboratoire: ${analysis.laboratoire}`,
            `Échantillon: ${analysis.echantillon}`,
            `Vanne associée: ${analysis.vanne_associee}`
        ];

        const titles = ['Paramètres d\'analyse des feuilles'];
        const tableIDs = ['leafAnalysisTable'];
        const filename = `Analyse_Foliaire_${analysisId}`;
        const user = `${userInfo.first_name} ${userInfo.last_name}`;

        exportTableToExcel(info, tableIDs, filename, titles, null, user);
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="bg-white h-full">
            <Header title="Modifier une Analyse Foliaire" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`${userLink()}/exploitation/${exploitationId}/analysis/feuilles`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />
                    <div className="font-semibold text-base text-indigo-600">Retour</div>
                </Link>

                {loading && <Loading />}
                {error && <div className="text-red-500">{error}</div>}

                {!loading && !error && analysis && (
                    <>
                        <div className="flex justify-end mb-4">
                            <div
                                className="flex gap-3 border p-2 hover:bg-green-200 rounded-md transition-all cursor-pointer"
                                onClick={handleExport}
                            >
                                <span className="font-bold text-lg">Export :</span>
                                <img
                                    src={excelPng}
                                    alt=""
                                    className="w-8"
                                />
                            </div>
                        </div>

                        <Card className="mb-6 max-w-[900px] m-auto">
                            <h2 className="text-xl py-4 font-semibold">Informations générales</h2>
                            <div>
                                <InfoItem label="Date de réalisation" value={moment(analysis.date_realisation).format('DD/MM/YYYY')} />
                                <InfoItem label="Laboratoire" value={analysis.laboratoire} />
                                <InfoItem label="Échantillon" value={analysis.echantillon} />
                                <InfoItem label="Vanne associée" value={analysis.vanne_associee} />
                            </div>
                        </Card>

                        <Card className="mb-6 max-w-[900px] m-auto">
                            <h2 className="text-xl py-4 font-semibold">Paramètres d'analyse des feuilles</h2>
                            <table id="leafAnalysisTable" className="w-full border-collapse border border-gray-300">
                                <thead>
                                    <tr>
                                        <HeaderTab text="Paramètre" />
                                        <HeaderTab text="Valeur" />
                                        <HeaderTab text="Min" />
                                        <HeaderTab text="Max" />
                                        <HeaderTab text="Unité" />
                                    </tr>
                                </thead>
                                <tbody className='text-center'>
                                    {leafAnalysisParameters.map(param => (
                                        <tr key={param.key}>
                                            <td className="border border-gray-300 px-2 text-left py-2">{param.label}</td>
                                            <td className="border border-gray-300 px-2 py-2">
                                                {renderValue(analysis[`${param.key}_data`])}
                                            </td>
                                            <td className="border border-gray-300 px-2 py-2">
                                                {renderValue(analysis[`${param.key}_min`])}
                                            </td>
                                            <td className="border border-gray-300 px-2 py-2">
                                                {renderValue(analysis[`${param.key}_max`])}
                                            </td>
                                            <td className="border border-gray-300 px-2 py-2">%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
};

const InfoItem = ({ label, value }) => (
    <div>
        <span className="font-semibold">{label}: </span>
        <span>{value}</span>
    </div>
);

const HeaderTab = ({ text }) => (
    <th className="border border-gray-300 bg-gray-300 px-2 table_heeader py-2">{text}</th>
);

export default ViewLeafAnalysis;